/**
 * Secciones de los productos de un evento ("Botellas", "Cócteles",
 * "Cervezas"…): la caja las usa para agrupar y filtrar con un toque, sin
 * teclado. Es un texto libre por producto (`section` en el brick); aquí
 * viven las reglas puras para nombrarlas, compararlas y agruparlas.
 */
import { normalizeText } from '../product-import/product-import.model';

/** Sugerencias al crear productos; cada evento puede escribir las suyas. */
export const DEFAULT_SECTIONS = ['Botellas', 'Cócteles', 'Mocktails', 'Cervezas', 'Sin alcohol', 'Shots', 'Vinos', 'Comida'];

/** Clave del grupo de productos sin sección (nunca colisiona con un nombre real). */
export const NO_SECTION_KEY = '__none__';
export const NO_SECTION_LABEL = 'Sin sección';

/** "Cócteles", "cocteles " y "COCTELES" son la misma sección. */
export function sectionKey(name: any): string {
    return normalizeText(name);
}

/** Nombre tal como se guarda y se muestra: sin espacios sobrantes y con la inicial en mayúscula. */
export function cleanSectionName(name: any): string {
    const clean = String(name === null || name === undefined ? '' : name).replace(/\s+/g, ' ').trim();
    return clean ? clean.charAt(0).toUpperCase() + clean.slice(1) : '';
}

export interface SectionGroup<T> {
    key: string;
    name: string;
    items: T[];
}

/**
 * Agrupa conservando el orden de entrada dentro de cada grupo (así el orden
 * por precio se mantiene). Grupos en orden alfabético; "Sin sección" al
 * final. El nombre que se muestra es el primero que aparece con esa clave.
 */
export function groupBySection<T>(items: T[], sectionOf: (item: T) => any): SectionGroup<T>[] {
    const groups: { [key: string]: SectionGroup<T> } = {};
    items.forEach(item => {
        const name = cleanSectionName(sectionOf(item));
        const key = name ? sectionKey(name) : NO_SECTION_KEY;
        if (!groups[key]) groups[key] = { key, name: name || NO_SECTION_LABEL, items: [] };
        groups[key].items.push(item);
    });
    return Object.keys(groups).map(k => groups[k]).sort((a, b) => {
        if (a.key === NO_SECTION_KEY) return 1;
        if (b.key === NO_SECTION_KEY) return -1;
        return a.name.localeCompare(b.name, 'es', { sensitivity: 'base' });
    });
}

/** Une listas de nombres sin repetir (misma clave), conservando el primer nombre visto. */
export function mergeSectionNames(...lists: string[][]): string[] {
    const seen: { [key: string]: boolean } = {};
    const out: string[] = [];
    lists.forEach(list => (list || []).forEach(raw => {
        const name = cleanSectionName(raw);
        const key = sectionKey(name);
        if (!name || seen[key]) return;
        seen[key] = true;
        out.push(name);
    }));
    return out;
}
